"use client";

import { IResource } from "@gettactic/api";
import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTacticHooks } from "../../providers/TacticHooksProvider";

export interface UseResourcesByIdsResult {
  resources: IResource[];
  isLoading: boolean;
}

/**
 * Resolve resources from their ids alone — for callers that hold a stored list
 * of resource ids (a saved allow-list, a bookmark) and need the resource's
 * office or floor to place it. One cached query per id, sharing the cache with
 * {@link useResource}.
 */
export function useResourcesByIds(
  resourceIds: string[]
): UseResourcesByIdsResult {
  const { api } = useTacticHooks();
  const idsKey = Array.from(new Set(resourceIds)).sort().join(",");
  const ids = useMemo(() => (idsKey ? idsKey.split(",") : []), [idsKey]);

  const results = useQueries({
    queries: ids.map((resourceId) => ({
      queryKey: ["resource", resourceId],
      queryFn: async () => {
        const res = await api.client.organizations.resourceGet(resourceId);
        return res.result?.resource ?? null;
      }
    }))
  });

  return useMemo(
    () => ({
      resources: results
        .map((result) => result.data)
        .filter((resource): resource is IResource => !!resource),
      isLoading: results.some((result) => result.isPending)
    }),
    [results]
  );
}
